import { useEffect } from 'react';
import { useCanonical } from '@/hooks/useCanonical';
import salonImg from '@/assets/salon-accueil.png';
import { Heart, Award, Users, GraduationCap, Stethoscope, Sparkles, HandHeart } from 'lucide-react';
import { motion } from 'framer-motion';
import CTASection from '@/components/CTASection';
import { PLANITY_URL } from '@/data/services';

const stats = [
  { icon: Heart, label: 'Patients accompagnés', value: '500+' },
  { icon: Award, label: 'Note Google', value: '4.5/5' },
  { icon: GraduationCap, label: 'Diplômes', value: '2' },
];

const parcours = [
  {
    icon: Stethoscope,
    title: 'Infirmière diplômée',
    text: 'Plusieurs années d\'exercice en libéral, au plus près des patients et de leurs problématiques de santé au quotidien.',
  },
  {
    icon: GraduationCap,
    title: 'Spécialisation esthétique médicale',
    text: 'Diplôme d\'infirmière en esthétique et micropigmentation capillaire obtenu à la Clinique des Champs-Élysées (formation Amir Aesthetic).',
  },
  {
    icon: HandHeart,
    title: 'Le déclic du bien-être',
    text: 'En observant le mal-être de ses patients — poils incarnés, kystes pilonidaux, perte de confiance liée au corps — Carine a voulu aller plus loin pour les accompagner.',
  },
  {
    icon: Sparkles,
    title: 'Body by CC',
    text: 'Le cabinet esthétique est né comme une extension naturelle du cabinet infirmier, pour offrir un accompagnement plus global centré sur le bien-être.',
  },
];

const valeurs = [
  {
    title: 'Bien-être avant tout',
    desc: 'L\'esthétique chez Body by CC n\'est pas du camouflage. C\'est un moyen d\'aider chacun à retrouver confiance en soi et à se sentir mieux dans son corps.',
  },
  {
    title: 'Bienveillance',
    desc: 'Chaque personne est accueillie avec attention et respect. Les soins sont réalisés dans un cadre chaleureux, sans jugement, avec une écoute sincère.',
  },
  {
    title: 'Rigueur médicale',
    desc: 'Le parcours d\'infirmière de Carine garantit des soins réalisés avec une exigence professionnelle, une hygiène irréprochable et des protocoles sécurisés.',
  },
];

const APropos = () => {
  useCanonical('/a-propos');
  useEffect(() => {
    document.title = 'À propos — Carine Tchuente, infirmière & esthéticienne | Body by CC';
    document.querySelector('meta[name="description"]')?.setAttribute('content', 'Découvrez le parcours de Carine Tchuente, infirmière diplômée spécialisée en esthétique médicale. Body by CC à Sevran : un institut centré sur le bien-être, la confiance en soi et l\'accompagnement bienveillant.');
  }, []);

  return (
    <>
      {/* Hero */}
      <section className="px-6 lg:px-8 py-20 md:py-28 bg-petrol text-petrol-foreground">
        <div className="max-w-7xl mx-auto">
          <span className="text-xs font-heading font-medium text-accent uppercase tracking-[0.2em]">Notre histoire</span>
          <h1 className="font-heading text-3xl md:text-5xl font-bold mt-4 leading-tight">Une infirmière engagée<br />dans le bien-être</h1>
          <p className="mt-5 text-petrol-foreground/60 max-w-2xl text-base md:text-lg leading-relaxed font-body">
            Body by CC est né de la conviction profonde que prendre soin de son corps, c'est prendre soin de soi.
            Fondé par Carine Tchuente, infirmière diplômée, l'institut place le <strong className="text-petrol-foreground/80">bien-être et la confiance en soi</strong> au cœur de chaque soin.
          </p>
        </div>
      </section>


      {/* Stats */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto grid grid-cols-3 gap-3 md:gap-5">
          {stats.map((s) => (
            <div key={s.label} className="text-center p-4 md:p-6 rounded-xl border border-border bg-card">
              <s.icon size={18} className="text-primary mx-auto mb-2" />
              <p className="font-heading text-xl md:text-2xl font-bold text-foreground">{s.value}</p>
              <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5 font-body">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Parcours de Carine */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-card">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground text-center">Le parcours de Carine</h2>
          <p className="text-center text-muted-foreground mt-3 max-w-xl mx-auto font-body text-sm">
            De l'infirmerie libérale à l'esthétique médicale : un chemin guidé par l'écoute et l'envie d'accompagner.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {parcours.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="flex items-start gap-4 p-5 rounded-2xl border border-border bg-background"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <p.icon size={18} className="text-primary" />
                </div>
                <div>
                  <p className="font-heading font-bold text-sm text-foreground">{p.title}</p>
                  <p className="text-xs text-muted-foreground font-body mt-1 leading-relaxed">{p.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Citation / Philosophie */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-secondary/30">
        <div className="max-w-3xl mx-auto text-center">
          <blockquote className="font-heading text-xl md:text-2xl font-bold text-foreground italic leading-relaxed">
            « Mon objectif, c'est le bien-être des gens. Ce n'est pas l'esthétique dans le camouflage, c'est l'esthétique dans le bien-être. »
          </blockquote>
          <p className="mt-4 text-sm text-muted-foreground font-body">— Carine Tchuente, fondatrice de Body by CC</p>
        </div>
      </section>

      {/* Valeurs */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground text-center">Nos valeurs</h2>
          <div className="w-8 h-0.5 bg-accent mx-auto mt-3 mb-10" />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {valeurs.map((v) => (
              <div key={v.title} className="p-6 rounded-xl border border-border bg-card">
                <h3 className="font-heading font-semibold text-base text-foreground">{v.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed font-body">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approche */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-card">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-6 md:p-8 rounded-2xl border border-primary/20 bg-background"
          >
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-4">Notre approche</h2>
            <div className="space-y-3 text-sm text-muted-foreground leading-relaxed font-body">
              <p>
                Chez Body by CC, chaque soin est réalisé dans une démarche de <strong className="text-foreground">santé et de bien-être</strong>, jamais de transformation à tout prix.
                L'esthétique est simplement un moyen supplémentaire d'aider chacun à retrouver confiance en soi.
              </p>
              <p>
                Le cabinet esthétique est une extension naturelle du cabinet infirmier de Carine. Cette continuité garantit une approche
                <strong className="text-foreground"> professionnelle, bienveillante et respectueuse</strong> de l'équilibre entre santé et esthétique.
              </p>
              <p>
                Équipé de machines dernier cri — HIFU, E-SLIM Cryo 360°, Skincleantech, photothérapie LED — l'institut propose des soins
                à la pointe de la technologie, toujours encadrés par une expertise médicale rigoureuse.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Photo du salon */}
      <section className="px-6 lg:px-8 py-10 md:py-14 bg-background">
        <div className="max-w-xs mx-auto">
          <div className="rounded-xl overflow-hidden border border-border shadow-md">
            <img
              src={salonImg}
              alt="Accueil de l'institut Body by CC à Sevran"
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </div>
          <p className="text-center text-[10px] text-muted-foreground mt-2 font-body">Notre institut — 65 Bd Westinghouse, Sevran</p>
        </div>
      </section>

      <CTASection
        title="Envie de prendre soin de vous ?"
        subtitle="Prenez rendez-vous et offrez-vous un moment de bien-être. Diagnostic personnalisé offert pour tout premier soin."
        buttonLabel="Prendre rendez-vous"
        buttonLink={PLANITY_URL}
        isExternal
      />
    </>
  );
};

export default APropos;
